import { fromUnixTime, isAfter, isBefore } from 'date-fns'

import { MAX_SAVE_32BIT_INT } from '@/constants/common'
import { ApiService } from '@/service'
import useStore from '@/store/useStore'
import { ChannelType, EventType } from '@/types/servicesTypes/configTypes'
import { ChannelEventsType } from '@/types/servicesTypes/linesTypes'
import { showFetchTimeInfo } from '@/utils/utils'

type ChannelEventTimeouts = {
  channelId: string
  timeoutId: NodeJS.Timeout
}

type UpdateChannelEventsFunction = (
  channelIds: string[],
  channelEventTimeouts: ChannelEventTimeouts[],
) => Promise<void>

const { appConfig, setChannelEvent } = useStore.getState().common

export const updateChannelEvents = async (
  isAppHidden: boolean,
  channels: ChannelType[],
) => {
  const start = performance.now()
  const channelIds = channels.map(({ id }) => id)

  await updateChannelEventsCache(isAppHidden, channelIds)
  const end = performance.now()
  showFetchTimeInfo(`Update Channel Events`, start, end)
}

const updateChannelEventsCacheDecorator = (
  updateCurrentChannelEvents: UpdateChannelEventsFunction,
) => {
  const channelEventTimeouts: ChannelEventTimeouts[] = []
  const cache = new Map<string, boolean>()

  return async function (isAppHidden: boolean, channelIds: string[]) {
    if (isAppHidden) {
      cache.clear()
      channelEventTimeouts.forEach((timeout) => clearTimeout(timeout.timeoutId))
      channelEventTimeouts.length = 0

      return
    }

    const newChannelIds = channelIds.filter(
      (channelId) => !cache.has(channelId),
    )

    if (newChannelIds.length > 0) {
      newChannelIds.forEach((channelId) => cache.set(channelId, true))
      await updateCurrentChannelEvents(newChannelIds, channelEventTimeouts)
    }
  }
}

const updateCurrentChannelEvents = async (
  channelIds: string[],
  channelEventTimeouts: ChannelEventTimeouts[],
) => {
  const currentChannelsEvents = await getCurrentChannelEventsByChannelIds(
    channelIds,
  )

  currentChannelsEvents?.forEach(({ channelId, event }) => {
    updateCurrentChannelEvent(channelId, event, channelEventTimeouts)
  })
}

const updateChannelEventsCache = updateChannelEventsCacheDecorator(
  updateCurrentChannelEvents,
)

const updateCurrentChannelEvent = async (
  channelId: string,
  eventData: Nullable<{
    event: EventType
    isNearestEvent: boolean
  }>,
  channelEventTimeouts: ChannelEventTimeouts[],
) => {
  let remainingEventTime = 0
  const { event, isNearestEvent } = eventData || {}

  let currentEvent: Nullable<EventType> = null
  if (event) {
    currentEvent = { ...event }
    if (event.categoryId) {
      try {
        const category = await ApiService.getSportCategoryById(event.categoryId)
        currentEvent.categoryName = category.name
      } catch (error) {
        console.error('Error fetching category name:', error)
      }
    }
    if (isNearestEvent) {
      remainingEventTime = event.startTime * 1000 - Date.now()
      currentEvent.isFutureChannelEvent = true
    } else {
      remainingEventTime = event.endTime * 1000 - Date.now()
    }
  }

  setChannelEvent(channelId, currentEvent)

  if (remainingEventTime <= 0 || remainingEventTime >= MAX_SAVE_32BIT_INT) {
    remainingEventTime = appConfig.refreshInterval * 1000
  }

  const completedTimeoutIndex = channelEventTimeouts.findIndex(
    (timeout) => timeout.channelId === channelId,
  )

  if (completedTimeoutIndex !== -1) {
    channelEventTimeouts.splice(completedTimeoutIndex, 1)
  }

  const timeoutId = setTimeout(
    () => updateCurrentChannelEventByChannelId(channelId, channelEventTimeouts),
    remainingEventTime,
  )

  channelEventTimeouts.push({ timeoutId, channelId })

  // const name = event?.name
  // window.timeouts.push({ timeoutId, channelId, name, remainingEventTime })
}

const updateCurrentChannelEventByChannelId = async (
  channelId: string,
  channelEventTimeouts: ChannelEventTimeouts[],
) => {
  const event = await getCurrentEventByChannelId(channelId)
  updateCurrentChannelEvent(channelId, event, channelEventTimeouts)
}

const getCurrentOrNearestEvent = (events: ChannelEventsType) => {
  if (!events.items.length) {
    return null
  }

  const currentEvent = events.items.find(
    (event) =>
      event &&
      isAfter(new Date(), fromUnixTime(event.startTime)) &&
      isBefore(new Date(), fromUnixTime(event.endTime)),
  )

  return {
    event: currentEvent || events.items[0],
    isNearestEvent: !currentEvent,
  }
}

const mockEvent = {
  userRights: { canAccess: true },
  id: 'tvprofil$100846262982829',
  categoryId: 'soccer',
  channelId: 'sw$insightzone-soccer-bundesliga-de',
  name: 'Insight Zone Bundesliga: 1. FC Union Berlin - SC Freiburg',
  startTime: 1731094200,
  endTime: 1731101400,
  description: {
    long: 'Live ist nur einmal.\nSpieltag 10: Mit innovativen Live-Statistiken zu allen Spielen in Echtzeit verpasst du keinen wichtigen Moment auf dem Platz.',
  },
  image: {
    sources: [
      {
        format: 'Jpeg',
        width: 1280,
        height: 720,
        url: 'https://tvprofil.net/cdn/1280x720/9/img/covers/img2271564-m326676-sport_event.jpg',
        darkTheme: false,
      },
    ],
  },
  live: true,
  type: 'Channel',
  nativewaves: {
    enableSync: false,
  },
  shareable: true,
  video: {
    sources: [
      {
        format: 'Hls',
        url: 'https://cdn-uw2-prod.tsv2.amagi.tv/linear/amg00622-b1smarttvgmbh-insightzonebundesligadach-ono/playlist.m3u8?app_bundle=REPLACE_ME&app_name=sportworld&app_store_url=REPLACE_ME&genre=soccer&ic=IAB17&gdpr=REPLACE_ME&gdpr_consent=REPLACE_ME&url=REPLACE_ME&us_privacy=REPLACE_ME&lmt=REPLACE_ME&dnt=REPLACE_ME&cb=REPLACE_ME&ap=REPLACE_ME&mute=REPLACE_ME&encoded_url=REPLACE_ME',
      },
    ],
  },
  deepLink: {
    ios: {
      url: 'insightzone-soccer-bundesliga-de',
    },
    android: {
      url: 'insightzone-soccer-bundesliga-de',
    },
  },
}

export const getCurrentEventByChannelId = async (id: string) => {
  try {
    const events = await ApiService.getEventsByChannelId(id)

    const isEnabledChannelEventMockData =
      useStore.getState().common.appSettings.isEnabledChannelEventMockData
    if (isEnabledChannelEventMockData) {
      const channelEvent = useStore.getState().common.channelEvents[id]
      const currentEvent =
        (!channelEvent?.isFutureChannelEvent && channelEvent) || null
      const mockEvents = [
        {
          ...mockEvent,
          startTime: Date.now() / 1000 + (currentEvent ? 10 : 0),
          endTime: Date.now() / 1000 + (currentEvent ? 20 : 10),
        } as EventType,
      ]
      events.items = mockEvents
    }

    return getCurrentOrNearestEvent(events)
  } catch (e) {
    return null
  }
}

const getCurrentChannelEventsByChannelIds = async (channelIds: string[]) => {
  try {
    const { channelEvents } = await ApiService.getBatchChannelEvents(channelIds)

    const isEnabledChannelEventMockData =
      useStore.getState().common.appSettings.isEnabledChannelEventMockData
    if (isEnabledChannelEventMockData) {
      const mockEvents = [
        {
          ...mockEvent,
          startTime: Date.now() / 1000 + 20,
          endTime: Date.now() / 1000 + 20,
        } as EventType,
      ]

      return channelEvents?.map(({ channelId, events }) => {
        events.items = mockEvents
        return {
          channelId,
          event: getCurrentOrNearestEvent(events),
        }
      })
    }

    return channelEvents?.map(({ channelId, events }) => ({
      channelId,
      event: getCurrentOrNearestEvent(events),
    }))
  } catch (e) {
    return null
  }
}
